"use client";

import { useState, useMemo, useCallback } from "react";
import { CarComparisonData } from "@interfaces/CarComparison.types";
import CarSelectionModal from "@components/molecules/CarSelectionModal";
import CarComparisonMobileTable from "@components/molecules/CarComparisonMobileTable";

interface CarComparisonMobileClientProps {
  cars: any[];
  config: any;
  showCompareCTA: boolean;
  brandName: string;
  modelName: string;
  cityName: string;
  carAvailability: string;
  pageType: string;
  isLoggedIn: boolean;
}

export default function CarComparisonMobileClient({
  cars,
  config,
  showCompareCTA,
  brandName,
  modelName,
  cityName,
  carAvailability,
  pageType,
  isLoggedIn,
}: CarComparisonMobileClientProps) {
  const maxMobile = 2;
  const maxSpecs = Math.max(
    ...(cars?.slice(0, maxMobile).map((car: any) => car.specs?.length || 0) || [
      0,
    ])
  );

  const [selectedCars, setSelectedCars] = useState<CarComparisonData[]>(() => {
    return cars.slice(0, maxMobile);
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const availableCars = useMemo(() => {
    return cars.slice(1);
  }, [cars]);

  const handleEditCar = useCallback((index: number) => {
    setEditingIndex(index);
    setIsModalOpen(true);
  }, []);

  const handleSelectCar = useCallback(
    (car: CarComparisonData) => {
      if (editingIndex !== null) {
        setSelectedCars((prev) => {
          const newSelectedCars = [...prev];
          newSelectedCars[editingIndex] = car;
          return newSelectedCars;
        });
        setIsModalOpen(false);
        setEditingIndex(null);
      }
    },
    [editingIndex]
  );

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setEditingIndex(null);
  }, []);

  return (
    <>
      <CarComparisonMobileTable
        selectedCars={selectedCars}
        config={config}
        showCompareCTA={showCompareCTA}
        handleEditCar={handleEditCar}
        maxMobile={maxMobile}
        maxSpecs={maxSpecs}
        brandName={brandName}
        modelName={modelName}
        cityName={cityName}
        carAvailability={carAvailability}
        pageType={pageType}
      />

      {/* ✅ Client-rendered modal */}
      <CarSelectionModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        availableCars={availableCars}
        onSelectCar={handleSelectCar}
        title="Select a car to compare"
        currentlySelectedCar={
          editingIndex !== null
            ? cars[editingIndex] || selectedCars[editingIndex]
            : null
        }
        brandName={brandName}
        modelName={modelName}
        cityName={cityName}
        isLoggedIn={isLoggedIn}
        carAvailability={carAvailability}
        pageType={pageType}
      />
    </>
  );
}
